import {
  type Condition,
  ConditionOperatorEnum,
  type FileSnapshot,
  type InventoryResource,
  SecurityScanConclusion,
  SecurityScanConclusionEnum,
  type Snapshot,
} from '@repo/api-gw-sdk';

import { SECURITY_SCAN_CONCLUSION_PROPERTY } from '@/components/queryBuilder/propertyNames';

// Returns true only for ransomware conclusions (not Malware or DATA_ANOMALY).
// Use this where the skull icon / "ransomware indicators" copy should appear.
export const hasRansomwareIssue = (entity: InventoryResource): boolean => {
  if ('securityScans' in entity && entity.securityScans?.length) {
    return entity.securityScans.some(
      (scan) =>
        scan.conclusion === SecurityScanConclusionEnum.HighChanceForRansomware
    );
  }
  return false;
};

// Inventory filter condition for resources flagged HIGH for ransomware. Uses
// the same property/operator/value the inventory page filters on (see
// ransomwareSummaryPieChart.tsx) so the serialized `query` round-trips through
// both the inventory page and the bulk-restore wizard's deep-link reader.
export const buildHighRansomwareCondition = (): Condition => ({
  property: SECURITY_SCAN_CONCLUSION_PROPERTY,
  operator: ConditionOperatorEnum.In,
  value: [SecurityScanConclusionEnum.HighChanceForRansomware],
});

export const buildThreatFindingsCondition = (): Condition => ({
  property: SECURITY_SCAN_CONCLUSION_PROPERTY,
  operator: ConditionOperatorEnum.In,
  value: [
    SecurityScanConclusionEnum.HighChanceForRansomware,
    SecurityScanConclusionEnum.MalwareDetected,
    SecurityScanConclusionEnum.DataAnomalyDetected,
  ],
});

export const hasSecurityIssue = (
  entity: InventoryResource | Snapshot | FileSnapshot
) => {
  if ('securityScans' in entity && entity.securityScans?.length) {
    return entity.securityScans.some(
      (scan) =>
        scan.conclusion && scan.conclusion !== SecurityScanConclusionEnum.Clean
    );
  }
  if (
    'securityScanConclusions' in entity &&
    entity.securityScanConclusions?.length
  ) {
    return entity.securityScanConclusions.some(
      (conclusion) => conclusion !== SecurityScanConclusion.Clean
    );
  }
  if ('securityScan' in entity && entity.securityScan?.conclusion) {
    return entity.securityScan.conclusion !== SecurityScanConclusionEnum.Clean;
  }
  return 'isInfected' in entity && !!entity.isInfected;
};
